import { authFetch, handleSessionExpired } from './authService';

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:8001'}/api/v1`;

async function handleResponse<T>(response: Response): Promise<{ data: T }> {
  if (response.status === 401) {
    // authFetch a déjà tenté le refresh — session réellement expirée.
    handleSessionExpired(response);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
    throw new Error(error.detail || 'Erreur réseau');
  }

  const data = await response.json();
  return { data };
}

export interface OverviewMetrics {
  global_precision: number;
  quality_score: number;
  total_documents: number;
  validated_documents: number;
  documents_with_corrections: number;
  errors_by_severity: Record<string, number>;
  errors_by_type: Record<string, number>;
  by_domain: Record<string, { precision: number; count: number; quality_score: number }>;
  by_model: Record<string, { precision: number; count: number; version: string }>;
  trend_30_days: Array<{ date: string; precision: number; quality_score: number }>;
}

export interface FieldPrecision {
  field_path: string;
  precision: number;
  total: number;
  errors: number;
  error_types: Record<string, number>;
}

export interface Suggestion {
  severity: number;
  severity_label: string;
  message: string;
  affected_fields: string[];
  error_count: number;
}

export interface WorstOcrPage {
  document_id: string;
  filename: string;
  page: number;
  avg_ocr_confidence: number;
  error_count: number;
  error_types: Record<string, number>;
}

export interface ModelPerformance {
  model_name: string;
  model_version: string | null;
  precision: number;
  documents: number;
  avg_processing_time_ms: number | null;
  errors_by_severity: Record<string, number>;
}

export const analyticsService = {
  getOverview: async (domain?: string) => {
    const url = domain
      ? `${API_BASE}/analytics/overview?domain=${domain}`
      : `${API_BASE}/analytics/overview`;
    const response = await authFetch(url);
    return handleResponse<OverviewMetrics>(response);
  },

  getFieldPrecision: async (domain: string) => {
    const response = await authFetch(`${API_BASE}/analytics/fields/${domain}`);
    return handleResponse<FieldPrecision[]>(response);
  },

  getSuggestions: async (domain: string) => {
    const response = await authFetch(`${API_BASE}/analytics/suggestions/${domain}`);
    return handleResponse<Suggestion[]>(response);
  },

  getWorstOcrPages: async (limit = 10) => {
    const response = await authFetch(`${API_BASE}/analytics/worst-ocr-pages?limit=${limit}`);
    return handleResponse<WorstOcrPage[]>(response);
  },

  getModelPerformance: async () => {
    const response = await authFetch(`${API_BASE}/analytics/model-performance`);
    return handleResponse<ModelPerformance[]>(response);
  },

  analyzeAll: async (domain?: string) => {
    const params = domain ? `?domain=${encodeURIComponent(domain)}` : '';
    const response = await authFetch(`${API_BASE}/analytics/analyze-all${params}`, { method: 'POST' });
    return handleResponse<{ analyzed: number }>(response);
  },
};
